import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  Text,
} from "@chakra-ui/react";
import { FiChevronDown, FiFilter } from "react-icons/fi";
import { DateInputWithLabel } from "newui/components/ui/InputWithLabel/DateInputWithLabel";
import dayjs from "newui/utils/dayjs";

export interface DateRangeValue {
  from: string | null;
  to: string | null;
}

export interface DateRangeFilterProps {
  label: string;
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
}

export const DateRangeFilter = ({ label, value, onChange }: DateRangeFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const fromDate = value.from ? dayjs(value.from).toDate() : null;
  const toDate = value.to ? dayjs(value.to).toDate() : null;

  const handleFromChange = (date: Date | null) => {
    onChange({ ...value, from: date ? dayjs(date).format("YYYY-MM-DD") : null });
  };

  const handleToChange = (date: Date | null) => {
    onChange({ ...value, to: date ? dayjs(date).format("YYYY-MM-DD") : null });
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange({ from: null, to: null });
  };

  const isActive = value.from !== null || value.to !== null;

  return (
    <Menu isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <MenuButton
        as={Button}
        rightIcon={<FiChevronDown />}
        leftIcon={<FiFilter size={14} />}
        variant="ghost"
        size="xs"
        onClick={() => setIsOpen(!isOpen)}
        _hover={{ bg: "gray.100" }}
        _active={{ bg: "gray.200" }}
        data-testid={`date-range-filter-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Text fontSize="xs">{label}</Text>
          {isActive && (
            <Badge colorScheme="blue" borderRadius="full" fontSize="2xs" px={2}>
              1
            </Badge>
          )}
        </Box>
      </MenuButton>
      <MenuList p={3} minW="260px">
        <Box display="flex" flexDirection="column" gap={3}>
          <DateInputWithLabel
            label="From"
            value={fromDate}
            onChange={handleFromChange}
            testId="date-range-filter-from"
          />
          <DateInputWithLabel
            label="To"
            value={toDate}
            onChange={handleToChange}
            testId="date-range-filter-to"
          />
          {isActive && (
            <>
              <Divider />
              <Button
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={handleClear}
                data-testid={`date-range-filter-clear-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                Clear Filter
              </Button>
            </>
          )}
        </Box>
      </MenuList>
    </Menu>
  );
};
